<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { UiIcon, UiText, type ExpertReviewSchema } from '@shopexperts/shared';
import type { z } from 'zod';
import reviewShareLogo from '@/assets/images/review-share-logo.svg?url';
import reviewShareVerified from '@/assets/images/review-share-verified.svg?url';

const props = defineProps<{ review: z.infer<typeof ExpertReviewSchema>; background: string }>();
const emit = defineEmits<{ loaded: []; error: [] }>();
const quote = ref<HTMLElement | null>(null);
const fontSize = ref(17.5);
const ratingWidth = computed(() => {
    const rating = Math.max(0, Math.min(5, props.review.overall_rating));
    return Math.min(110, Math.floor(rating) * 22.5 + (rating % 1) * 20);
});
const reviewer = computed(() => props.review.is_anonymous ? 'Anonymous client' : props.review.client_name);

async function fitQuote() {
    await document.fonts.ready;
    fontSize.value = 17.5;
    await nextTick();
    if (!quote.value) return;
    // Reserve the header, footer, padding and gaps within the 25px image margins.
    // Short reviews keep their natural height; long reviews shrink only as needed.
    let low = 1;
    let high = 17.5;
    for (let step = 0; step < 10 && quote.value; step++) {
        if (quote.value.scrollHeight <= 162) low = fontSize.value;
        else high = fontSize.value;
        fontSize.value = (low + high) / 2;
        await nextTick();
    }
    fontSize.value = low;
}

defineExpose({ fitQuote });

onMounted(fitQuote);
watch(() => props.review.review_text, fitQuote);
</script>

<template>
    <div class="review-share-artwork" inert>
        <img :key="background" :src="background" alt="" class="review-share-background" @load="emit('loaded')" @error="emit('error')" />
        <div class="review-share-card">
            <div class="review-share-heading">
                <div class="review-share-rating" :aria-label="`${review.overall_rating} out of 5 stars`">
                    <UiIcon name="review-rating-color" class="review-share-rating-icon grayscale opacity-20" />
                    <div class="review-share-rating-fill" :style="{ width: `${ratingWidth}px` }">
                        <UiIcon name="review-rating-color" class="review-share-rating-icon" />
                    </div>
                </div>
                <div class="review-share-dot" />
                <div class="review-share-reviewer">
                    <UiText as="span" variant="override" class="review-share-name" :text="reviewer" />
                    <img v-if="review.is_hired_on_shopexperts" :src="reviewShareVerified" class="review-share-badge" alt="Verified Review" />
                </div>
            </div>
            <div ref="quote" class="review-share-quote" :style="{ fontSize: `${fontSize}px`, lineHeight: `${fontSize * 9 / 7}px` }">
                <UiText as="p" variant="override"><span>{{ review.review_text }}</span></UiText>
            </div>
            <img :src="reviewShareLogo" alt="shopexperts.com" class="review-share-logo" />
        </div>
    </div>
</template>

<style scoped>
@font-face { font-family: ReviewShareArchivo; src: url('@/assets/fonts/review-share-archivo.ttf') format('truetype'); font-weight: 400; font-style: normal; }
.review-share-artwork { position: relative; width: 600px; height: 337.5px; overflow: hidden; background: #f3f3f3; font-family: ReviewShareArchivo, sans-serif; color: #1f2125; transform-origin: top left; }
.review-share-background { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.review-share-card { position: absolute; left: 25px; right: 25px; top: 50%; transform: translateY(-50%); display: flex; flex-direction: column; gap: 17.5px; padding: 25px; border-radius: 12.5px; background: white; }
.review-share-heading { display: flex; align-items: center; gap: 7.5px; height: 20px; }
.review-share-rating { position: relative; width: 110px; height: 20px; flex-shrink: 0; }
.review-share-rating-icon { display: block; width: 110px; height: 20px; max-width: none; }
.review-share-rating-fill { position: absolute; inset-block: 0; left: 0; overflow: hidden; }
.review-share-reviewer { display: flex; align-items: center; gap: 5px; min-width: 0; }
.review-share-dot { width: 5px; height: 5px; flex-shrink: 0; border-radius: 50%; background: #d6d6d6; }
/* Align the name baseline at 45px within the 64px header. */
:deep(.review-share-name) { min-width: 0; font-family: ReviewShareArchivo, sans-serif; transform: translateY(0.9375px); letter-spacing: 0; font-size: 12.5px; line-height: 15px; font-weight: 400; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.review-share-badge { flex-shrink: 0; width: 63.125px; height: 15px; }
.review-share-logo { margin-left: auto; width: 145.625px; height: 19.6875px; flex-shrink: 0; }
.review-share-quote { overflow-wrap: anywhere; }
.review-share-quote :deep(p) { font-family: inherit; font-size: inherit; line-height: inherit; font-weight: 400; }
</style>
